import { __rest } from "tslib";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import classNames from 'classnames';
import isEmpty from 'lodash/fp/isEmpty';
import isEqual from 'lodash/fp/isEqual';
import compact from 'lodash/fp/compact';
import noop from 'lodash/fp/noop';
import AutoSuggest from '../autosuggest/AutoSuggest';
import CustomSuggestionItem from './CustomSuggestionItem';
import TagManagerItemList from './TagManagerItemList';
const getCustomSuggestion = (value, placeholder) => ({
    customSuggestion: _jsx(CustomSuggestionItem, { value: value, placeholder: placeholder }),
    label: value,
    disabled: isEmpty(value),
});
const getSuggestionSeparator = (dropdownSeparatorText) => ({
    header: true,
    label: dropdownSeparatorText,
});
const cleanupTagState = (tag) => {
    delete tag.toAdd;
    delete tag.toRemove;
    return tag;
};
const hasTagAlreadySelected = (tags, newSuggestion) => !!tags.find(tag => tag.label === newSuggestion.label);
const getNewSuggestion = (suggestion) => {
    const newSuggestion = suggestion.customSuggestion ? { label: suggestion.label } : suggestion;
    newSuggestion.toAdd = true;
    return newSuggestion;
};
const buildSuggestions = (useCustomTags, customTagPlaceholder, dropdownSeparatorText) => (value, suggestions) => {
    const cleanedSuggestions = suggestions.map(cleanupTagState).filter(item => item.label);
    return compact([
        useCustomTags && getCustomSuggestion(value, customTagPlaceholder),
        useCustomTags &&
            !isEmpty(cleanedSuggestions) &&
            !isEmpty(dropdownSeparatorText) &&
            getSuggestionSeparator(dropdownSeparatorText),
        ...cleanedSuggestions,
    ]);
};
const filterSuggestions = (suggestions, selectedTagNames, value = '') => suggestions.filter(suggestion => {
    const label = suggestion.label;
    if (!label) {
        return false;
    }
    const lowerCaseLabel = label.toLowerCase();
    const lowerCaseValue = value.toLowerCase();
    return (lowerCaseLabel.includes(lowerCaseValue) &&
        !selectedTagNames.includes(label) &&
        !suggestion.header &&
        !suggestion.customSuggestion);
});
const TagManager = (props) => {
    const { tagList, tagSuggestions, placeholder, className, customTagPlaceholder, dropdownSeparatorText, onTagListChange = noop, useCustomTags = true, noItemMessage, showInput = true } = props, remainingProps = __rest(props, ["tagList", "tagSuggestions", "placeholder", "className", "customTagPlaceholder", "dropdownSeparatorText", "onTagListChange", "useCustomTags", "noItemMessage", "showInput"]);
    const [tags, setTags] = useState(tagList);
    const [inputValue, setInputValue] = useState('');
    const [currentSuggestions, setCurrentSuggestions] = useState([]);
    const getSuggestions = buildSuggestions(useCustomTags, customTagPlaceholder, dropdownSeparatorText);
    // Update internal list of tags when they change from the outside i.e. when they have been saved,
    // additionally, cleanup previous state for adding or removing tags
    useEffect(() => setTags(compact(tagList.map(cleanupTagState))), [tagList]);
    // build suggestion when passed tag suggestions passed change i.e. when a tag got selected and removed from the list
    useEffect(() => setCurrentSuggestions(getSuggestions(inputValue, tagSuggestions)), [tagSuggestions]);
    // build suggestions when input value change as it should filter the list on suggestions
    useEffect(() => {
        const selectedTagNames = compact(tags.map(tag => tag.label));
        const value = inputValue || '';
        const filteredList = filterSuggestions(tagSuggestions, selectedTagNames, value);
        setCurrentSuggestions(getSuggestions(value, filteredList));
    }, [tags, inputValue, tagSuggestions]);
    const updateTags = (updatedTags) => {
        setTags(updatedTags);
        // Notify outside component
        console.log(updatedTags);
        onTagListChange(updatedTags);
    };
    const handleSuggestionsChange = ({ value }) => setInputValue(value);
    const handleSuggestionSelected = (_, { suggestion }) => {
        setInputValue('');
        const newSuggestion = getNewSuggestion(suggestion);
        if (hasTagAlreadySelected(tags, newSuggestion)) {
            updateTags([...tags]);
            return;
        }
        updateTags([...tags, newSuggestion]);
    };
    const updateTagsWithSelectedTag = (tagItems, selectedTag) => {
        return tagItems.map(tag => {
            if (isEqual(tag.label, selectedTag.label)) {
                // update tag and mark as "to remove"
                if (tag.toRemove) {
                    delete tag.toRemove;
                }
                else {
                    tag.toRemove = true;
                }
            }
            return tag;
        });
    };
    const handleRemoveFromTagList = (selectedTag) => {
        if (selectedTag.toAdd) {
            updateTags(tags.filter(tag => !isEqual(tag, selectedTag)));
        }
        else {
            updateTags(updateTagsWithSelectedTag(tags, selectedTag));
        }
    };
    const inputProps = {
        className: '',
        placeholder: placeholder || 'Start typing ...',
        value: inputValue,
        tabIndex: 1,
    };
    const autoSuggestClasses = classNames(!isEmpty(tags) && 'margin-bottom-10');
    return (_jsxs("div", Object.assign({}, remainingProps, { className: classNames('TagManager', className) }, { children: [showInput && (_jsx(AutoSuggest, { className: autoSuggestClasses, inputProps: inputProps, suggestions: currentSuggestions, onSuggestionSelected: handleSuggestionSelected, onSuggestionsFetchRequested: handleSuggestionsChange, openOnFocus: true, noItemMessage: noItemMessage })), _jsx(TagManagerItemList, { tags: tags, onRemoveFromTagList: handleRemoveFromTagList })] })));
};
export default TagManager;
